// Playlist downloads are packaged by the video service (Smrt-Stats/video-platform)
// while the browser waits on a single request, and the service's router closes
// any request after 120 s. A long playlist therefore never finishes in one
// request. These helpers split a download into parts that each fit, prepare the
// parts one after another, and hand every finished file to the user through a
// click of their own, so a slow response can't vanish behind a pop-up blocker.

// Parts are sized by footage, not clip count: cutting, downloading back, zipping
// and uploading all grow with video length. Starting values - tune them from the
// video-cut logs (time between "Zipping N videos" and "Upload success").
export const MAX_PART_SECONDS = 20 * 60;
export const MAX_PART_CLIPS = 40;

// The service rejects the whole request with 422 when any clip comes from another
// host (video-cut/src/http/dto/request.py).
export const VIDEO_SOURCE_PREFIX = 'https://s3.smrtstats.com';

// The router gives up after 120 s; stop waiting shortly after in case it doesn't.
export const PART_REQUEST_TIMEOUT_MS = 150 * 1000;

// After a timeout the service keeps packaging the part and caches the result, so
// a later retry usually returns it at once. Retrying sooner would start a second
// job for the same clips while the first is still writing them, and the service
// shares local file names between jobs, which can corrupt the ZIP.
export const PART_RETRY_DELAYS_MS = [90 * 1000, 180 * 1000];

const GATEWAY_STATUSES = [502, 503, 504];

const decodePath = (path) => {
  try {
    return decodeURI(path);
  } catch (error) {
    return path;
  }
};

// Turns playlist videos into clips the video service accepts, keeping playlist
// order. Clips it would reject go to `skipped` instead of failing the whole
// download: another host fails validation, and a start before 0:00 (an event in
// the first seconds of a video) makes ffmpeg fail, so the start is clamped.
export function prepareDownloadCuts(videos) {
  const cuts = [];
  const skipped = [];

  (Array.isArray(videos) ? videos : []).forEach((video, index) => {
    const rawPath = video?.details?.video;
    const path = typeof rawPath === 'string' ? decodePath(rawPath.trim()) : '';
    const hasTimes = video?.start_second != null && video?.end_second != null;
    const start = Math.max(0, Math.floor(Number(video?.start_second)));
    const end = Math.ceil(Number(video?.end_second));

    if (!path.startsWith(VIDEO_SOURCE_PREFIX)) {
      skipped.push({ index, reason: 'source' });
    } else if (!hasTimes || !Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      skipped.push({ index, reason: 'time' });
    } else {
      cuts.push({ index, path, start, end });
    }
  });

  return { cuts, skipped };
}

// Groups clips into consecutive parts without reordering them. A clip longer than
// the limit still gets a part of its own.
export function splitIntoParts(cuts, { maxSeconds = MAX_PART_SECONDS, maxClips = MAX_PART_CLIPS } = {}) {
  return (Array.isArray(cuts) ? cuts : []).reduce((parts, cut) => {
    const seconds = cut.end - cut.start;
    const last = parts[parts.length - 1];

    if (last && last.cuts.length < maxClips && last.seconds + seconds <= maxSeconds) {
      last.cuts.push(cut);
      last.seconds += seconds;
    } else {
      parts.push({ number: parts.length + 1, cuts: [cut], seconds });
    }

    return parts;
  }, []);
}

// The request body the video service expects for one clip.
export const toCutRequest = ({ path, start, end }) => ({ end, path, start });

// 1215 -> "20:15", 4830 -> "1:20:30"
export function formatFootage(totalSeconds) {
  const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const pad = (value) => String(value).padStart(2, '0');

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds % 60)}`
    : `${minutes}:${pad(seconds % 60)}`;
}

const isCanceled = (error) => error?.code === 'ERR_CANCELED'
  || error?.name === 'CanceledError'
  || error?.name === 'AbortError';

// Worth retrying: no response at all (a timeout, a dropped connection, or the
// router's own 504, which the browser hides behind a CORS error) and gateway
// errors. Anything else - a 422, or a 500 after the service already retried each
// clip - fails the same way again.
export function isRetryableDownloadError(error) {
  if (!error || isCanceled(error)) {
    return false;
  }

  const status = error.response?.status;
  return !status || GATEWAY_STATUSES.includes(status);
}

export function describeDownloadError(error, subject = 'this part') {
  const status = error?.response?.status;

  if (!status || GATEWAY_STATUSES.includes(status)) {
    return `The video service didn't finish preparing ${subject} in time. It keeps working in the background, so try again in a few minutes.`;
  }

  if (status === 422) {
    return `The video service rejected a clip in ${subject}.`;
  }

  const reason = error.response?.data?.errors?.[0];
  const detail = typeof reason === 'string' && reason ? `: ${reason.slice(0, 140)}` : ` (error ${status}).`;
  return `The video service couldn't prepare ${subject}${detail}`;
}

const wait = (ms, signal) => new Promise((resolve) => {
  if (signal?.aborted) {
    resolve();
    return;
  }

  const finish = () => {
    clearTimeout(timer);
    signal?.removeEventListener('abort', finish);
    resolve();
  };
  const timer = setTimeout(finish, ms);
  signal?.addEventListener('abort', finish);
});

// Requests one part until it is ready, retrying only the errors a retry can fix.
// Every state change goes through onChange(update). Resolves to true when the part
// is ready and false when it failed or was stopped.
export async function preparePart({
  part,
  requestPart,
  onChange,
  signal,
  retryDelaysMs = PART_RETRY_DELAYS_MS,
  sleep = wait,
}) {
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    onChange({ status: 'preparing', attempt, error: null });

    try {
      const url = await requestPart(part, signal);

      if (!url) {
        onChange({ status: 'failed', error: "The video service didn't return a file for this part." });
        return false;
      }

      onChange({ status: 'ready', url, error: null });
      return true;
    } catch (error) {
      if (signal?.aborted) {
        onChange({ status: 'stopped' });
        return false;
      }

      if (!isRetryableDownloadError(error) || attempt === retryDelaysMs.length) {
        onChange({ status: 'failed', error: describeDownloadError(error) });
        return false;
      }

      onChange({ status: 'retrying', retryInSeconds: Math.round(retryDelaysMs[attempt] / 1000) });
      await sleep(retryDelaysMs[attempt], signal);

      if (signal?.aborted) {
        onChange({ status: 'stopped' });
        return false;
      }
    }
  }

  return false;
}

// Browsers only let a page open a tab shortly after a click, and a file is often
// ready later than that. Open it while the click still counts; otherwise, or when
// the browser blocks it anyway, call offer(url) so the user can start the download
// with a click of their own.
export function openDownloadOrOffer(url, offer) {
  const activation = typeof navigator !== 'undefined' ? navigator.userActivation : undefined;

  if (!activation || activation.isActive) {
    const tab = window.open(url, '_blank');
    if (tab) {
      return true;
    }
  }

  offer(url);
  return false;
}
